import { ui } from "@patient-watch/ui";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { usePainSliderCopy } from "@/features/i18n/painSliderCopy";
import Description from "../../../common/Description";
import { useRanger, type Ranger } from "@tanstack/react-ranger";
import { parseSliderText, sliderCommittedValue, sliderRestValue } from "../../sliderValue";
import PainFaceIcon from "./PainFaceIcon";
import PainScaleKey from "./PainScaleKey";
import { sliderFace } from "./sliderFace";

interface Props {
    label?: string;
    name: string;
    disabled?: boolean;
    required?: boolean;
}

const PAIN_MIN = 0;
const PAIN_MAX = 10;
const PAIN_REST = sliderRestValue(PAIN_MIN, PAIN_MAX);

const MemoizedPainFaceIcon = React.memo(PainFaceIcon);

const clampPain = (value: number): number => {
    if (value > PAIN_MAX) return PAIN_MAX;
    if (value < PAIN_MIN) return PAIN_MIN;
    return value;
};

const PainSlider = ({ label, name, disabled, required = false }: Props) => {
    const copy = usePainSliderCopy();
    const form = useFormContext();
    const { control, formState } = form;
    const committed = sliderCommittedValue(form.watch(name));
    const [values, setValues] = useState<ReadonlyArray<number>>([committed ?? PAIN_REST]);
    const rangerRef = useRef<HTMLDivElement>(null);
    const formOnChange = useRef<(v: number | null) => void>();
    const pointerMoved = useRef(false);

    const rangerInstance = useRanger<HTMLDivElement>({
        getRangerElement: () => rangerRef.current,
        values,
        min: PAIN_MIN,
        max: PAIN_MAX,
        stepSize: 1,
        onDrag: (instance: Ranger<HTMLDivElement>) => {
            const next = instance.sortedValues[0];
            const current = values[0] ?? PAIN_REST;
            // Mouseup also calls onDrag. Ignore it unless the thumb has actually moved.
            if (next === undefined || (next === current && !pointerMoved.current)) return;
            pointerMoved.current = true;
            setValues(instance.sortedValues);
            formOnChange.current?.(next);
        },
        onChange: (instance: Ranger<HTMLDivElement>) => {
            const next = instance.sortedValues[0];
            pointerMoved.current = false;
            if (next === undefined) return;
            // A click on the resting thumb is not an answer. Arrow keys and a finished drag are.
            if (committed == null && next === PAIN_REST) return;
            setValues(instance.sortedValues);
            formOnChange.current?.(next);
        },
    });

    useEffect(() => {
        setValues([committed ?? PAIN_REST]);
    }, [committed]);

    const thumbValue = values[0] ?? PAIN_REST;
    const hasAnswer = committed != null;

    return (
        <ui.Card className="flex flex-col justify-center items-center gap-4 mx-auto px-0 w-full">
            <ui.CardHeader className="flex flex-col justify-center items-center px-4">
                <ui.Label
                    className={cn(
                        "font-normal text-slate-800 text-2xl text-center",
                        label?.length > 15 && "text-xl py-2",
                        label?.length > 25 && "text-lg pt-2 pb-4"
                    )}
                >
                    {label ?? "Pain Score"}
                    {required && <span className="ml-0.5 text-red-500">*</span>}
                </ui.Label>

                <PainScaleKey />
                <p className="max-w-md text-muted-foreground text-sm text-center">{copy.scoringHint}</p>
            </ui.CardHeader>
            <ui.CardContent className="flex flex-col gap-y-12 mb-8 w-full">
                <Controller
                    name={name}
                    control={control}
                    render={({ field: { onChange } }) => {
                        formOnChange.current = onChange;
                        return (
                            <>
                                <ui.Input
                                    min={PAIN_MIN}
                                    max={PAIN_MAX}
                                    data-testid="number-input"
                                    onChange={(e) => {
                                        const parsed = parseSliderText(e.target.value);
                                        if (parsed === undefined) return;
                                        if (parsed === null) {
                                            setValues([PAIN_REST]);
                                            onChange(null);
                                            return;
                                        }
                                        const next = clampPain(parsed);
                                        setValues([next]);
                                        onChange(next);
                                    }}
                                    value={hasAnswer ? Math.round(committed).toString() : ""}
                                    className="flex justify-center items-center m-auto p-auto w-[70px] h-[70px] text-4xl text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
                                    type="number"
                                    disabled={disabled}
                                />
                                <div ref={rangerRef} className="relative w-full h-3 touch-none select-none">
                                    <div className="absolute inset-0 bg-indigo-100 dark:bg-indigo-800 rounded-full" />
                                    {rangerInstance
                                        .handles()
                                        .map(
                                            (
                                                { value, onKeyDownHandler, onMouseDownHandler, onTouchStart, isActive },
                                                i
                                            ) => (
                                                <React.Fragment key={i}>
                                                    <div
                                                        className="absolute bg-indigo-800 dark:bg-indigo-50 rounded-full h-full"
                                                        style={{
                                                            width: `${rangerInstance.getPercentageForValue(value)}%`,
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onKeyDown={onKeyDownHandler}
                                                        onMouseDown={onMouseDownHandler}
                                                        onTouchStart={onTouchStart}
                                                        aria-label={hasAnswer ? `Pain level ${value}` : "Pain level"}
                                                        disabled={disabled}
                                                        className={cn(
                                                            "top-1/2 absolute flex justify-center items-center rounded-full outline-none w-12 h-12 -translate-x-1/2 -translate-y-1/2 cursor-grab",
                                                            isActive && "cursor-grabbing z-10",
                                                            disabled && "opacity-50 pointer-events-none"
                                                        )}
                                                        style={{
                                                            left: `${rangerInstance.getPercentageForValue(value)}%`,
                                                        }}
                                                    >
                                                        <MemoizedPainFaceIcon value={value} />
                                                    </button>
                                                </React.Fragment>
                                            )
                                        )}
                                </div>
                                {hasAnswer ? (
                                    <Description className="mx-auto text-center">
                                        {sliderFace(thumbValue)?.message}
                                    </Description>
                                ) : null}
                            </>
                        );
                    }}
                />
            </ui.CardContent>
            <ui.CardFooter>
                {formState.errors[name] && (
                    <p className="text-red-500">
                        {(formState.errors[name]?.message as string) ?? "Please select a value."}
                    </p>
                )}
            </ui.CardFooter>
        </ui.Card>
    );
};

export default PainSlider;
